/**
 * Company Search — resolved-company dashboard (sealed state 2, elements a–h).
 *
 * Composes the sealed shape in order: entity header (a) → relationship band
 * (b) → the three-column row warm path (c) / internal contacts (d) / active
 * engagements (f) → pattern read (g), positioned between the band row and the
 * signals → signals at this company (e) → a personal RADAR tracker section (h).
 *
 * Every absent field renders honest absence; contract-gap elements (internal
 * contacts, pattern read, per-engagement freshness) render their empty state.
 * Personal RADAR tracking is available on every resolved profile, whether or
 * not the company already has an Ankura relationship or active signal.
 *
 * The Company Intelligence Brief (CIB Layer 1) is APPENDED after element h,
 * per the ratified R-UI-1 placement: appending leaves the sealed a-h sequence
 * and every existing element's position untouched.
 */

import type { ProfileView } from "../types";
import { EntityHeader } from "./EntityHeader";
import { RelationshipBand } from "./RelationshipBand";
import { WarmPathCard } from "./WarmPathCard";
import { InternalContacts } from "./InternalContacts";
import { Engagements } from "./Engagements";
import { PatternRead } from "./PatternRead";
import { LinkedSignals } from "./LinkedSignals";
import { RadarTracker } from "./RadarTracker";
import { CompanyBrief } from "./CompanyBrief";

export function CompanyProfile({
  profile,
  matchBasis,
}: {
  profile: ProfileView;
  matchBasis: string | null;
}): JSX.Element {
  return (
    <div className="sxco-profile">
      <EntityHeader displayName={profile.displayName} matchBasis={matchBasis} />

      <RelationshipBand
        existingClient={profile.existingClient}
        engagementCount={profile.engagementCount}
        hasCaseStudy={profile.hasCaseStudy}
        valueTier={profile.valueTier}
      />

      <div className="sxco-cols">
        <WarmPathCard warmPath={profile.warmPath} />
        <InternalContacts organizationId={profile.organizationId} />
        <Engagements engagements={profile.engagements} />
      </div>

      <PatternRead organizationId={profile.organizationId} />

      <LinkedSignals organizationId={profile.organizationId} />

      <RadarTracker
        organizationId={profile.organizationId}
        companyName={profile.displayName ?? "Company"}
      />

      <CompanyBrief organizationId={profile.organizationId} companyName={profile.displayName} />
    </div>
  );
}
